import { evaluate, NoReturnError, parseFormula, roundToDp, seriesLookupFrom, type TSeriesLookup } from "@libs/ledger-formula";
import type { TLedgerFigure } from "@/zapi-frontend/shared/types/modelLedger";

export type TLedgerGraph = {
  byKey: Map<string, TLedgerFigure>;
  // Evaluation order: every key appears after the keys its formula reads.
  order: string[];
  dependents: Map<string, string[]>;
  cycles: string[];
};

export type TSolvedLedger = {
  values: Map<string, number>;
  // Keys whose formula could not be evaluated (cycle, missing dependency, bad expression).
  unresolved: string[];
  // Keys whose return has no value at these settings: flows that never cross zero have no IRR, a
  // total that never recovers has no payback. Shown as a dash that says so, never as the
  // published number, which would claim a rate the case does not have.
  noReturn: string[];
};

// The generation already resolved each formula's references into `dependsOn`, so the graph is
// built from that rather than re-parsing: one source of truth for what a figure reads.
export const buildLedgerGraph = (figures: TLedgerFigure[]): TLedgerGraph => {
  const byKey = new Map(figures.map((figure) => [figure.key, figure]));
  const deps = new Map<string, string[]>();
  const dependents = new Map<string, string[]>();

  for (const figure of figures) {
    const own = figure.formula ? figure.dependsOn.filter((key) => byKey.has(key)) : [];
    deps.set(figure.key, own);
    for (const dep of own) {
      dependents.set(dep, [...(dependents.get(dep) ?? []), figure.key]);
    }
  }

  // Kahn's algorithm; whatever is left when the queue drains sits in a cycle.
  const remaining = new Map([...deps].map(([key, list]) => [key, list.length]));
  const queue = [...remaining].filter(([, count]) => count === 0).map(([key]) => key);
  const order: string[] = [];
  while (queue.length > 0) {
    const key = queue.shift() as string;
    order.push(key);
    for (const dependent of dependents.get(key) ?? []) {
      const left = (remaining.get(dependent) ?? 0) - 1;
      remaining.set(dependent, left);
      if (left === 0) queue.push(dependent);
    }
  }
  const cycles = [...remaining].filter(([, count]) => count > 0).map(([key]) => key);

  return { byKey, order, dependents, cycles };
};

// Recomputes the whole ledger with the given stated figures replaced. Cheap enough to run on
// every slider frame: one pass over an already sorted list of a few hundred keys.
// `series(stem)` reads a family of yearly rows in year order, through the same lookup the ledger
// was stored with, so a formula means the same thing on this page as it did there.
export const seriesLookupOf = (graph: TLedgerGraph): TSeriesLookup =>
  seriesLookupFrom([...graph.byKey.values()].filter((figure) => figure.kind !== "relation").map((figure) => figure.key));

export const solveLedger = (graph: TLedgerGraph, overrides: Record<string, number> = {}): TSolvedLedger => {
  const values = new Map<string, number>();
  const unresolved: string[] = [];
  const noReturn: string[] = [];
  const seriesMembers = seriesLookupOf(graph);
  const env: Record<string, number> = {};

  for (const key of graph.order) {
    const figure = graph.byKey.get(key);
    if (!figure) continue;
    // A relation is a claim the ledger checks, not a number the page draws: its `formula` holds a
    // comparison, and reading it as arithmetic reports every one of them as a figure we failed on.
    if (figure.kind === "relation") continue;
    const override = overrides[key];
    if (override !== undefined && Number.isFinite(override)) {
      values.set(key, override);
      env[key] = override;
      continue;
    }
    if (!figure.formula) {
      if (figure.value !== null) {
        values.set(key, figure.value);
        env[key] = figure.value;
      } else unresolved.push(key);
      continue;
    }
    let computed: number | null = null;
    let returnMissing = false;
    try {
      const result = evaluate(parseFormula(figure.formula), env, seriesMembers);
      // A series is a value in the grammar but never a figure's own value. Rounded to the figure's
      // own decimals before anything downstream reads it, which is the ledger's own rule: without
      // it a chain of computed figures drifts away from the numbers the analysis published.
      // Only where the ledger says what precision it printed: a ledger written before `dp` was
      // carried was not rounded step by step, and rounding it now walks away from its own numbers.
      if (typeof result === "number" && Number.isFinite(result)) {
        computed = typeof figure.dp === "number" ? roundToDp(result, figure.dp) : result;
      }
    } catch (err) {
      computed = null;
      returnMissing = err instanceof NoReturnError;
    }
    if (computed === null && returnMissing) {
      // No value to show and none to hold: the published rate belongs to other settings.
      noReturn.push(key);
      unresolved.push(key);
      continue;
    }
    if (computed === null) {
      // Shown as the published value, so the page keeps its shape, and the box says it is held.
      // NOT fed onwards: a figure computed from a stale input is stale too, and it would carry no
      // mark at all — the page would read as live where it is a mixture. Leaving the key out of
      // `env` fails everything downstream in turn, so the whole cone is marked rather than one row.
      if (figure.value !== null) values.set(key, figure.value);
      unresolved.push(key);
      continue;
    }
    values.set(key, computed);
    env[key] = computed;
  }

  for (const key of graph.cycles) {
    if (!unresolved.includes(key)) unresolved.push(key);
  }

  return { values, unresolved, noReturn };
};

// Every figure that has to be redrawn when `key` moves, including `key` itself.
export const affectedBy = (graph: TLedgerGraph, key: string): Set<string> => {
  const affected = new Set<string>([key]);
  const queue = [key];
  while (queue.length > 0) {
    for (const dependent of graph.dependents.get(queue.shift() as string) ?? []) {
      if (affected.has(dependent)) continue;
      affected.add(dependent);
      queue.push(dependent);
    }
  }
  return affected;
};
